/**
 * LooseFkTreatAsEnumField — the loose-FK editor's "Also treat as enum"
 * checkbox, sitting directly under the Label-column row.
 *
 * Presentational only: the host (`LooseRelationshipTargetPicker`) decides
 * WHETHER to render it (see its `canTreatAsEnum`) and what a tick means at
 * save time. Sibling of `LooseFkDisplayColumnSelect` — same reason for
 * existing: keep the picker's already-long form body readable.
 *
 * The control is never rendered disabled by the host: when declaring wouldn't
 * be honest (unknown or oversized target, a source filter, a JSON path, an
 * existing declaration) the whole field is absent instead. A greyed-out
 * checkbox in a form this dense reads as noise, not as an explanation.
 */

import { Checkbox } from '@/components/ui/checkbox';

export type LooseFkTreatAsEnumFieldProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Only while a save / delete is in flight — not an eligibility signal. */
  disabled?: boolean;
  /** Qualified referenced table, e.g. `dbo.Statuses` (`qualifyLookupTarget`). */
  targetLabel: string;
  /** Live, unfiltered row count of the referenced table. */
  targetRowCount: number;
};

export function LooseFkTreatAsEnumField({
  checked,
  onCheckedChange,
  disabled,
  targetLabel,
  targetRowCount,
}: LooseFkTreatAsEnumFieldProps) {
  return (
    <div className="flex items-start gap-2" data-testid="loose-fk-treat-as-enum-row">
      <Checkbox
        id="loose-fk-treat-as-enum"
        checked={checked}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        disabled={disabled}
        className="mt-0.5"
        data-testid="loose-fk-treat-as-enum"
      />
      <div className="flex flex-col gap-0.5 min-w-0">
        <label
          htmlFor="loose-fk-treat-as-enum"
          className="text-[11px] leading-snug cursor-pointer"
        >
          Also treat as enum — show names in grids, filters, and cell editing
        </label>
        <p
          className="text-[10px] text-muted-foreground/80 leading-snug"
          data-testid="loose-fk-treat-as-enum-hint"
        >
          <span className="font-mono">{targetLabel}</span> has {targetRowCount} rows.
        </p>
      </div>
    </div>
  );
}
